import React, { useEffect, useMemo, useState } from 'react';
import TableContainer from 'Common/TableContainer';
import { Dropdown } from 'Common/Components/Dropdown';
import { MoreHorizontal, FileEdit, Trash2, Plus } from 'lucide-react';
import DeleteModal from 'Common/DeleteModal';
import Modal from 'Common/Components/Modal';
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  getEconomicActivityTypes,
  addEconomicActivityTypes,
  updateEconomicActivityTypes,
  deleteEconomicActivityTypes
} from "../../helpers/fakebackend_helper";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useLoader } from "../../context/LoaderContext";

interface EconomicActivityType {
  id: string;
  name: string;
  code: string;
  [key: string]: any;
}

const EconomicActivityTypes = () => {
  const [data, setData] = useState<EconomicActivityType[]>([]);
  const [eventData, setEventData] = useState<EconomicActivityType | null>(null);
  const [show, setShow] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const { showLoader, hideLoader } = useLoader();

  const fetchCategories = async () => {
    showLoader();
    try {
      const response = await getEconomicActivityTypes();
      const categoryData = response.data?._embedded?.economicActivityTypes || [];
      setData(categoryData);
    } catch (error) {
      toast.error("Error fetching economic activity types: " + error, { autoClose: 2000 });
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: '',
      code: ''
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Nombre es requerido"),
      code: Yup.string().required("Código es requerido"),
    }),

    onSubmit: async (values) => {
      showLoader();
      try {
        if (isEdit && eventData?.economicActivityTypeId) {
          await updateEconomicActivityTypes(eventData.economicActivityTypeId, { id: eventData.economicActivityTypeId, ...values });
          toast.success("Tipo de actividad actualizado correctamente", { autoClose: 2000 });
        } else {
          await addEconomicActivityTypes(values);
          toast.success("Tipo de actividad agregado correctamente", { autoClose: 2000 });
        }
        handleCloseModal();
        fetchCategories();
      } catch (error) {
        toast.error("Error saving economic activity types: " + error, { autoClose: 2000 });
      } finally {
        hideLoader();
      }
    }
  });

  useEffect(() => {
    if (isEdit && eventData) {
      formik.setValues({
        name: eventData.name || '',
        code: eventData.code || ''
      });
    } else if (!isEdit && !eventData) {
      formik.resetForm();
    }
  }, [eventData, isEdit]);

  const handleCloseModal = () => {
    setShow(false);
    setEventData(null);
    setIsEdit(false);
    formik.resetForm();
  };

  const handleCreateClick = () => {
    setEventData(null);
    setIsEdit(false);
    formik.resetForm();
    setShow(true);
  };

  const onClickDelete = (cell: EconomicActivityType) => {
    setEventData(cell);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    if (eventData?.economicActivityTypeId) {
      showLoader();
      try {
        await deleteEconomicActivityTypes(eventData.economicActivityTypeId);
        toast.success("Tipo de actividad eliminado correctamente", { autoClose: 2000 });
        fetchCategories();
      } catch (err) {
        toast.error("Error deleting economic activity type: " + err, { autoClose: 2000 });
      } finally {
        hideLoader();
        setEventData(null);
        setDeleteModal(false);
      }
    }
  };

  const handleUpdateDataClick = (ele: EconomicActivityType) => {
    setEventData(ele);
    setIsEdit(true);
    setShow(true);
  };

  const columns = useMemo(() => [
    {
      header: "Nombre",
      accessorKey: "name",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Código",
      accessorKey: "code",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Acciones",
      cell: ({ row }: any) => (
        <div className="flex justify-center">
          <Dropdown className="relative">
            <Dropdown.Trigger className="flex items-center justify-center size-8 rounded-full bg-slate-100 text-slate-500 hover:bg-slate-600 hover:text-white focus:outline-none">
              <MoreHorizontal className="size-4" />
            </Dropdown.Trigger>
            <Dropdown.Content className="absolute z-50 right-0 mt-2 w-40 bg-white rounded-md shadow-lg dark:bg-zink-600">
              <ul>
                <li>
                  <button
                    type="button"
                    onClick={() => handleUpdateDataClick(row.original)}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-zink-100 dark:hover:bg-zink-500"
                  >
                    <FileEdit className="inline size-4 mr-2" />
                    Editar
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => onClickDelete(row.original)}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-zink-100 dark:hover:bg-zink-500"
                  >
                    <Trash2 className="inline size-4 mr-2" />
                    Eliminar
                  </button>
                </li>
              </ul>
            </Dropdown.Content>
          </Dropdown>
        </div>
      ),
    }
  ], []);

  return (
    <div className='card'>
      <DeleteModal
        show={deleteModal}
        onHide={() => setDeleteModal(false)}
        onDelete={handleDelete}
      />


      <div className="card-body">
        <div className="flex justify-end mb-4">
          <button onClick={handleCreateClick} className="btn bg-custom-500 text-white">
            <Plus className="inline size-4 mr-1" /> Agregar Tipo de Actividad
          </button>
        </div>
      </div>

      <div className='card-body'>
        <TableContainer
          isPagination={true}
          isSelect={true}
          isGlobalFilter={true}
          columns={columns}
          data={data}
          customPageSize={5}
          divclassName="my-2 col-span-12 overflow-x-auto lg:col-span-12"
          tableclassName="hover group dataTable w-full text-sm align-middle whitespace-nowrap no-footer"
          theadclassName="border-b border-slate-200 dark:border-zink-500"
          trclassName="group-[.stripe]:even:bg-slate-50 group-[.stripe]:dark:even:bg-zink-600 transition-all duration-150 ease-linear group-[.hover]:hover:bg-slate-50 dark:group-[.hover]:hover:bg-zink-600 [&.selected]:bg-custom-500 dark:[&.selected]:bg-custom-500 [&.selected]:text-custom-50 dark:[&.selected]:text-custom-50"
          thclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500 sorting px-3 py-4 text-slate-900 bg-slate-200/50 font-semibold text-left dark:text-zink-50 dark:bg-zink-600 dark:group-[.bordered]:border-zink-500"
          tdclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500"
          PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
        />
      </div>

      <Modal show={show} onHide={handleCloseModal} modal-center
        className="fixed inset-0 z-drawer flex items-center justify-center"
        dialogClassName="w-screen max-w-md bg-white shadow-xl rounded-xl dark:bg-zink-600"
      >
        <Modal.Header className="flex items-center justify-between px-6 py-4 border-b dark:border-zink-500">
          <Modal.Title className="text-lg font-semibold text-slate-900 dark:text-zink-100">
            {isEdit ? "Editar Tipo de Actividad" : "Agregar Tipo de Actividad"}
          </Modal.Title>
          <button onClick={handleCloseModal} className="text-slate-400 hover:text-red-500 transition">
            ×
          </button>
        </Modal.Header>

        <Modal.Body className="p-6">
          <form onSubmit={formik.handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="name" className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100">Nombre</label>
              <input
                id="name"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                className="form-input w-full border border-slate-300 dark:border-zink-500 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-500"
              />
              {formik.touched.name && formik.errors.name && (
                <p className="text-sm text-red-500 mt-1">{formik.errors.name}</p>
              )}
            </div>

            <div>
              <label htmlFor="code" className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100">Código</label>
              <input
                id="code"
                name="code"
                value={formik.values.code}
                onChange={formik.handleChange}
                className="form-input w-full border border-slate-300 dark:border-zink-500 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-500"
              />
              {formik.touched.code && formik.errors.code && (
                <p className="text-sm text-red-500 mt-1">{formik.errors.code}</p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={handleCloseModal} className="btn bg-white text-red-500 border border-red-500 hover:bg-red-50 transition">
                Cancelar
              </button>
              <button type="submit" className="btn bg-custom-500 text-white hover:bg-custom-600 transition">
                {isEdit ? "Actualizar" : "Agregar"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default EconomicActivityTypes;